import axios from "axios";
import { clearLoggedOut } from "@/features/auth/authStorage";
import {
  getAuthApiBaseUrl,
  getAuthRequestConfig,
  getAuthURL,
  getBaseUrl,
  isLocalhost,
} from "@/services/api/baseUrl";

const ME_PROBE_MAX_RETRIES = 4;
const ME_PROBE_RETRY_DELAY_MS = 1500;

/**
 * Session identity comes from the invoice API's auth routes — the same host as
 * login, logout and status. The auth portal host is only used for profile links
 * and avatars.
 */

async function getMeOnce() {
  const response = await axios.get(`${getBaseUrl()}auth/me`, getAuthRequestConfig());
  return response.data;
}

export async function fetchCurrentUser() {
  for (let attempt = 0; attempt <= ME_PROBE_MAX_RETRIES; attempt += 1) {
    try {
      return await getMeOnce();
    } catch (error) {
      const isRateLimited = error?.response?.status === 429;
      if (isRateLimited && attempt < ME_PROBE_MAX_RETRIES) {
        await new Promise((resolve) => setTimeout(resolve, ME_PROBE_RETRY_DELAY_MS));
        continue;
      }
      throw error;
    }
  }

  return getMeOnce();
}

export async function postLogout() {
  if (isLocalhost()) return;
  await axios.post(`${getBaseUrl()}auth/logout`, null, getAuthRequestConfig());
}

export function getProfilePictureUrl(userGuid) {
  if (!userGuid) return null;
  return `${getAuthApiBaseUrl()}/companies/users/${userGuid}/profile-picture`;
}

export function getUserDetailsUrl(user) {
  if (!user?.guid) return null;
  const name = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || user.name || "";
  return `${getAuthURL()}/#/user-management/user-details/${encodeURIComponent(name)}/${user.guid}`;
}

/** Hands the browser to the SSO login endpoint on the invoice API host. */
export function redirectToSsoLogin() {
  if (isLocalhost()) {
    throw new Error(
      "Local development does not use SSO. Set VITE_JWT_TOKEN in .env.local.",
    );
  }

  clearLoggedOut();
  window.location.href = `${getBaseUrl()}auth/login`;
}
